import React, { FC, ReactNode } from 'react';
import {
  EnrichedService,
  ServiceListLayoutOptions,
} from '../../../../../types/types';
import { ReferralInfo } from '../../../../../utils/bi/consts';
import { useWidgetActions } from '../../hooks/useWidgetActions';
import { Card } from './Card/Card';
import { Strip } from './Strip/Strip';
import { Overlapping } from './Overlapping/Overlapping';
import { ServiceInfo } from './ServiceInfo/ServiceInfo';
import { ServiceImage } from './ServiceImage/ServiceImage';
import { useWidgetViewModel } from '../../hooks/useWidgetViewModel';
import { useVisibilityCheck } from '../../../hooks/useVisibilityCheck';
import { useAddVisibilityClass } from '../../../hooks/useAddVisibilityClass';
import { classes } from './ServiceCard.st.css';
import { LayoutCard } from './LayoutCard/LayoutCard';
import { LayoutImage } from './LayoutImage/LayoutImage';
import { useLayoutPerBpInWidget } from '../../../hooks/useLayoutPbpInWidget';
import { shouldOpenPreferencesModal } from '@wix/bookings-services-preferences-modal/utils';
import { FILTER_STAFF_BY_LOCATION_EXPERIMENT } from '@wix/bookings-services-preferences-modal/constants';
import { useSettings } from '@wix/tpa-settings/react';
import { useExperiments } from '@wix/yoshi-flow-editor';
import {
  ClickDestination,
  getServiceNameClickDestination,
} from '../../../../../utils/getClickDestination/getClickDestination';

export type ServiceCardProps = {
  service: EnrichedService;
  width: number;
  height: number;
  index: number;
};

export interface ServiceLayoutProps {
  image: ReactNode;
  info: ReactNode;
  index?: number;
}

function getCardComponent({
  serviceListLayout,
}: {
  serviceListLayout: ServiceListLayoutOptions;
}) {
  switch (serviceListLayout) {
    case ServiceListLayoutOptions.OVERLAPPING:
      return Overlapping;
    case ServiceListLayoutOptions.STRIP:
      return Strip;
    case ServiceListLayoutOptions.GRID:
    case ServiceListLayoutOptions.CLASSIC:
    default:
      return Card;
  }
}

export const ServiceCard: FC<ServiceCardProps> = (props) => {
  const { service, width, height, index } = props;
  const { serviceListLayout } = useWidgetViewModel();
  const { onServiceClick } = useWidgetActions();
  const { shouldBeVisible } = useVisibilityCheck();
  const { layoutStylePerBpEnable, layoutPbpPhase2Enabled } =
    useLayoutPerBpInWidget();
  const isServiceImageVisible =
    layoutStylePerBpEnable || shouldBeVisible('isServiceImageVisible');
  const { addVisibilityClassForLayoutPbp } = useAddVisibilityClass();
  const CardComponent = layoutPbpPhase2Enabled
    ? LayoutCard
    : getCardComponent({ serviceListLayout });

  const ImageComponent = layoutPbpPhase2Enabled ? LayoutImage : ServiceImage;
  const shouldNavigateImmediately = useShouldNavigateImmediately(service);
  const hrefUrl = useHrefUrl(service, shouldNavigateImmediately);

  return (
    <CardComponent
      index={index}
      info={
        <ServiceInfo
          service={service}
          shouldNavigateImmediately={shouldNavigateImmediately}
          hrefUrl={hrefUrl}
        />
      }
      image={
        isServiceImageVisible ? (
          <ImageComponent
            className={addVisibilityClassForLayoutPbp(
              '',
              classes.serviceImageVisibility,
              'isServiceImageVisible',
            )}
            image={service.media?.mainMedia?.image}
            onClick={() =>
              onServiceClick({
                service,
                referralInfo: ReferralInfo.SERVICE_IMAGE,
              })
            }
            imageTitle={service.name!}
            width={width}
            height={height}
            hrefUrl={hrefUrl}
          />
        ) : null
      }
    />
  );
};

const useShouldNavigateImmediately = (service: EnrichedService) => {
  const { bookingsSettings, queryParams } = useWidgetViewModel();
  const { experiments } = useExperiments();
  const { blockNavigationReason } = service;

  const isShouldOpenPreferencesModal = shouldOpenPreferencesModal({
    service,
    bookingsSettings,
    isDummyService: service.dummy,
    queryParams,
    isFilterStaffByLocationEnabled: experiments.enabled(
      FILTER_STAFF_BY_LOCATION_EXPERIMENT,
    ),
  });

  return !blockNavigationReason && !isShouldOpenPreferencesModal;
};

const useHrefUrl = (
  service: EnrichedService,
  shouldNavigateImmediately: boolean,
) => {
  const settings = useSettings();
  const { experiments } = useExperiments();
  const { coursesAvailability } = useWidgetViewModel();

  const destination = getServiceNameClickDestination({
    service,
    experiments,
    settings,
    numberOfSpotsLeft: coursesAvailability?.[service.id!]?.spotsLeft,
  });

  switch (destination) {
    case ClickDestination.SERVICE_PAGE:
      return service.urls?.servicePage?.url!;
    case ClickDestination.BOOKING_CALENDAR:
      return shouldNavigateImmediately
        ? service.urls?.calendarPage?.url!
        : undefined;
    // The booking form has no static URL (it is resolved at navigation time), so the
    // href is suppressed and the click handler drives navigateToBookingsFormPage.
    case ClickDestination.BOOKING_FORM:
      return undefined;
  }
};
